import * as React from "react";
import type { Stop } from "../types";
import { useAtom } from "jotai";
import { stopDetailsCacheAtom, stopDetailsInflightAtom } from "../atoms";
import { findStops, type StopFindResult } from "../api/stopFind";

function stripStopName(name: string) {
  return name.replace(/\s*[\(\[]?[A-ZÅÄÖ][\)\]]?$/, "").trim();
}

function getRootIdFromStop(stop: Stop): string {
  return String(stop.id).split("-")[0];
}

function toRad(deg: number) {
  return (deg * Math.PI) / 180;
}

function haversineMeters(
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number
) {
  const R = 6371000;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const la1 = toRad(aLat);
  const la2 = toRad(bLat);

  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return R * c;
}

function matchFindHitForStop(
  stop: Stop,
  hits: StopFindResult[]
): StopFindResult | null {
  if (!hits.length) return null;

  const baseName = stripStopName(stop.name).toLowerCase();

  const nameCandidates = hits.filter((h) => {
    const place = (h.PlaceName || "").toLowerCase();
    return place === baseName || place.startsWith(baseName + ",");
  });

  const candidates = nameCandidates.length ? nameCandidates : hits;
  if (!candidates.length) return null;

  const distances = candidates.map((hit) => {
    const [hitLat, hitLng] = hit.Ll;
    const d = haversineMeters(stop.lat, stop.lng, hitLat, hitLng);
    return { hit, dist: d };
  });

  distances.sort((a, b) => a.dist - b.dist);

  const best = distances[0];
  const second = distances[1];

  const MAX_ACCEPT_DIST = 80;
  const MIN_GAP = 50;

  if (!best || best.dist > MAX_ACCEPT_DIST) return null;
  if (second && second.dist - best.dist < MIN_GAP) return null;

  return best.hit;
}

export function useEnrichedStopName(stop: Stop): string {
  const [cache, setCache] = useAtom(stopDetailsCacheAtom);
  const [inflight, setInflight] = useAtom(stopDetailsInflightAtom);
  const [label, setLabel] = React.useState(stop.name);

  React.useEffect(() => {
    const baseName = stripStopName(stop.name);
    const rootId = getRootIdFromStop(stop);
    const key = `${rootId}#${baseName.toLowerCase()}`;

    if (cache.has(key)) {
      const hit = cache.get(key);
      const pretty = hit?.PlaceName ?? baseName;
      setLabel(pretty);
      return;
    }

    let cancelled = false;

    const running = inflight.get(key);
    if (running) {
      running.then((hit) => {
        if (cancelled) return;
        const pretty = hit?.PlaceName ?? baseName;
        setLabel(pretty);
      });
      return;
    }

    const promise: Promise<StopFindResult | null> = (async () => {
      try {
        const hits = await findStops(baseName, "stop");
        return matchFindHitForStop(stop, hits);
      } catch (e) {
        console.error("useEnrichedStopName: findStops failed", e);
        return null;
      }
    })();

    setInflight((prev) => {
      const next = new Map(prev);
      next.set(key, promise);
      return next;
    });

    promise.then((hit) => {
      if (cancelled) return;

      setCache((prev) => {
        const next = new Map(prev);
        next.set(key, hit);
        return next;
      });

      setInflight((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });

      const pretty = hit?.PlaceName ?? baseName;
      setLabel(pretty);
    });

    return () => {
      cancelled = true;
    };
  }, [
    stop.id,
    stop.name,
    stop.lat,
    stop.lng,
    cache,
    setCache,
    inflight,
    setInflight,
  ]);

  return label;
}
